import snarkdown from 'snarkdown';
/**
 * @description : Common methods for parcels.
 * @requires {Object} baseSixtyFour Polyfill to decode b64 strings.
 * @requires {Object} trackingService.
 * @requires {Object} $cookies
 */
function parcelsService(baseSixtyFour, trackingService, $cookies) {

  const mailPieceListIdsOrdered = {};

  /**
   * @description : Check if an object has a property, then return the object
   * property if yes, an empty string otherwise.
   * @param {Object} obj An object.
   * @param {String} prop A property.
   * @returns {Object|String} An object property or an empty string.
   */
  const isSet = (obj, prop) => {

    if (angular.isObject(obj) && Object.prototype.hasOwnProperty.call(obj, prop)) return obj[prop];

    return '';

  };

  /**
   * @description : Ordering of the mailpieces by timestamp.
   * @param {Object} mailPieceList - mailpiece object.
   */
  const orderMailPieces = (mailPieceList) => {

    // eslint-disable-next-line
    for (let a in mailPieceListIdsOrdered) delete mailPieceListIdsOrdered[a];

    // Sorting param array by timestamp last accessed.
    const sortedMailPieceList = mailPieceList.sort(
      (prev, next) => next.lastAccessedTimestamp - prev.lastAccessedTimestamp
    );

    let i = 0;
    for (i; i < sortedMailPieceList.length; i++) {

      mailPieceListIdsOrdered[i] =
        mailPieceList[i].mailPieceId;

    }

  };

  /**
   * @description : Call getHistory endpoint.
   * @param {String} guid A user global id.
   * @param {String} appId An application id.
   * @param {Number} limit The number of results we want to display.
   * @returns {Object} An object of ordered mailPieces Ids.
   */
  const getHistory = (guid, appId, limit) => trackingService.getHistory(guid, appId, limit)
    .then((getHistoryResponse) => {

      if (isSet(getHistoryResponse, 'data')) {

        if (isSet(getHistoryResponse.data, 'mpDetails')) {

          orderMailPieces(getHistoryResponse.data.mpDetails);
          return getHistoryResponse.data.jwt;

        }

      }

      return Promise.reject(getHistoryResponse);

    });

  /**
   * @description : Call getSummary endpoint.
   * @param {Object} getHistoryResponse The getHistory response object.
   * @returns {Object} An object of mailPieces Ids.
   */
  const getSummary = getHistoryResponse => trackingService.getSummary(getHistoryResponse).then((getSummaryResponse) => {

    const sortedData = {
      mailPieces: []
    };

    let i = 0;
    let j = 0;

    // Loop through the list of ordered mailPieces and get the corresponding
    // entry in the getSummary response. Then recreate an object of ordered
    // mailPieces to return.
    angular.forEach(mailPieceListIdsOrdered, (sortedMailPiecesIdValue) => {

      i = 0;
      for (i; i < getSummaryResponse.data.mailPieces.length; i++) {

        if (sortedMailPiecesIdValue === getSummaryResponse.data.mailPieces[i].mailPieceId) {

          sortedData.mailPieces[j] = getSummaryResponse.data.mailPieces[i];

        }

      }

      j++;

    });

    getSummaryResponse.data = sortedData;

    return getSummaryResponse;

  });

  /**
   * @description : Call getHistory then use the result to call getSummary then
   * return the getSummary response as a Promise.
   * @param {Number} guid A user global id.
   * @param {Number} appId An application id.
   * @param {Number} limit The number of results we want to display.
   * @returns {Promise} a getSummary promise.
   */
  const getRtiResults = (guid, appId, limit = 0) => getHistory(guid, appId, limit)
    .then(getHistoryResponse => getSummary(getHistoryResponse)
      .then(getSummaryResponse => getSummaryResponse));

  /**
   * @description : Decode a JWT token.
   * @param {String} token A token to decode.
   * @returns {String|Boolean} The decoded token or false.
   */
  const decodeJWT = (token) => {

    if (token && (token !== null) && (token !== undefined)) {

      const base64Url = token.split('.')[1];

      if ((base64Url !== null) && (base64Url !== undefined)) {

        return JSON.parse(baseSixtyFour.urlDecode(base64Url));

      }

      return false;

    }

    return false;

  };

  /**
   * @description : Checks if object exists on the list and compares if is equal.
   * @param {Object} objList The object list to iterate.
   * @param {Object} obj The object we want to check.
   * @returns {Boolean}
   */
  const contains = (objList, obj) => {

    let i = objList.length;
    while (i--) {

      if (JSON.stringify(objList[i]) === JSON.stringify(obj)) {

        return true;

      }

    }

    return false;

  };

  /**
   * @description: Check for matches in the nested members.
   * @param {Object} obj : the object being evaluated.
   * @param {String} args : comma separated values of 1 or more levels deep into the object.
   * @returns  {Boolean}
   */
  const checkNested = (obj, ...args) => {

    let nestedObj = obj;

    for (let i = 0; i < args.length; i += 1) {

      if (!nestedObj || !Object.prototype.hasOwnProperty.call(nestedObj, args[i])) {

        return false;

      }

      nestedObj = nestedObj[args[i]];

    }

    return true;

  };

  /**
   * @description : Formats a date object to 'dd/mm/yyyy', splits it and returns
   * the date and time objects.
   * @param {Object} date A date object.
   * @returns {Object} {date, time}.
   */
  const formatDate = (date) => {

    const isoDate = date.toISOString()
      .substr(0, 10)
      .split('-')
      .reverse()
      .join('/');

    const timeSplit = date.toString()
      .split(/:(.+)/)[1];

    return {
      date: isoDate,
      time: timeSplit.split(' ')[0]
    };

  };

  /**
   * @description: Checks the validation for a JWT token.
   * @param   {String} jwtToken : The encoded JWT we want to check.
   * @returns {Boolean} : If the token is valid.
   */
  const isValidSessionJWT = (jwtToken) => {

    // Transform the Date().getTime() from milliseconds to seconds.
    const now = Math.floor(new Date().getTime() / 1000);
    const JWT = (jwtToken) ? decodeJWT(jwtToken) : '';
    const exp = (isSet(JWT, 'exp') !== '') ? isSet(JWT, 'exp') : 0;

    return (JWT && parseInt(exp, 10) && exp > now);

  };

  /**
   * @description: Sets the state for Logged In cookie.
   * @returns {Boolean} : If the LoggedIn cookie is set and valid.
   */
  const isValidCookie = () => {

    const cookie = $cookies.get('parcels_jwt');

    if (cookie) {

      return isValidSessionJWT(cookie);

    }

    return false;

  };

  /**
   * @description: Converts Markdown to HTML.
   * @param {Object|String} mdObject : The the markdown object being translated.
   * @returns {String} : An HTML string.
   */
  const markdownToHTML = (mdObject = '') => {

    const html = snarkdown(mdObject);
    return html;

  };

  /**
   * @description: Remove the timezone from the original date.
   * @param {String} dateString A date in ISO 8601 format provided by the API as "2016-11-20T10:04:00+01:00".
   * @returns {string|*} date.
   */
  function ignoreTimezone(dateString) {

    return dateString.substr(0, 19);

  }

  return {
    getHistory,
    orderMailPieces,
    getSummary,
    getRtiResults,
    decodeJWT,
    isSet,
    contains,
    checkNested,
    formatDate,
    isValidCookie,
    isValidSessionJWT,
    markdownToHTML,
    ignoreTimezone
  };

}

export default parcelsService;



// WEBPACK FOOTER //
// ./src/angular-services/parcels/parcels.service.js